"use client";

import { c as _c } from "react/compiler-runtime";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import React, { useMemo } from "react";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { ChatRestriction, SubmissionRestriction } from "@/hooks/useDropPriviledges";
interface CreateDropDropModeToggleProps {
  readonly isDropMode: boolean;
  readonly onDropModeChange: (isDropMode: boolean) => void;
  readonly privileges: DropPrivileges;
  readonly exitLabel?: string | null;
  readonly inactiveActionLabel?: "drop" | "nominate";
}
export const CreateDropDropModeToggle: React.FC<CreateDropDropModeToggleProps> = t0 => {
  const $ = _c(27);
  const {
    isDropMode,
    onDropModeChange,
    privileges,
    exitLabel: t1,
    inactiveActionLabel: t2
  } = t0;
  const exitLabel = t1 === undefined ? null : t1;
  const inactiveActionLabel = t2 === undefined ? "drop" : t2;
  const {
    chatRestriction,
    submissionRestriction
  } = privileges;
  const hasExitLabel = exitLabel !== null;
  const hasChatRestriction = chatRestriction !== null;
  const hasSubmissionRestriction = submissionRestriction !== null;
  const isExitOnly = isDropMode && hasExitLabel;
  const isIdentityEntry = inactiveActionLabel === "nominate";
  const isDisabled = !isExitOnly && (isDropMode && hasChatRestriction || !isDropMode && hasSubmissionRestriction);
  const canToggle = isExitOnly || (isDropMode ? !hasChatRestriction : !hasSubmissionRestriction);
  const targetRestriction = isDropMode ? chatRestriction : submissionRestriction;
  let t3;
  if ($[0] !== canToggle || $[1] !== isDropMode || $[2] !== isExitOnly || $[3] !== onDropModeChange) {
    t3 = canToggle ? () => onDropModeChange(isExitOnly ? false : !isDropMode) : undefined;
    $[0] = canToggle;
    $[1] = isDropMode;
    $[2] = isExitOnly;
    $[3] = onDropModeChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleToggleClick = t3;
  const inactiveModeLabel = isIdentityEntry ? "Nominate" : "Drop";
  const targetModeLabel = isDropMode ? "Chat" : inactiveModeLabel;
  let t4;
  bb0: {
    if (isDisabled) {
      t4 = `${"tw-group tw-relative tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-sm tw-transition-all active:tw-scale-95 md:tw-h-10 md:tw-w-10 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2"} tw-opacity-50 tw-cursor-not-allowed ${isDropMode ? "tw-border tw-border-solid tw-border-primary-500/20 tw-bg-primary-500/10 tw-text-primary-400" : "tw-border tw-border-transparent tw-bg-iron-700 tw-text-iron-500"}`;
      break bb0;
    }
    if (isDropMode) {
      t4 = "tw-group tw-relative tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-sm tw-transition-all active:tw-scale-95 md:tw-h-10 md:tw-w-10 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 tw-border tw-border-solid tw-border-primary-500/20 tw-bg-primary-500/10 tw-text-primary-400 desktop-hover:hover:tw-border-primary-500/30 desktop-hover:hover:tw-bg-primary-500/20 desktop-hover:hover:tw-text-primary-400 focus-visible:tw-outline-primary-500";
      break bb0;
    }
    t4 = "tw-group tw-relative tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-sm tw-transition-all active:tw-scale-95 md:tw-h-10 md:tw-w-10 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 tw-border tw-border-transparent tw-bg-iron-700 tw-text-iron-300 desktop-hover:hover:tw-bg-iron-700/70 focus-visible:tw-outline-iron-500";
  }
  const buttonClassName = t4;
  let t5;
  if ($[5] !== inactiveActionLabel) {
    t5 = (restriction, isChat) => {
      const actionLabel = isChat ? inactiveActionLabel : "chat";
      switch (restriction) {
        case ChatRestriction.NOT_LOGGED_IN:
        case SubmissionRestriction.NOT_LOGGED_IN:
          {
            return `Please log in to ${actionLabel}`;
          }
        case ChatRestriction.PROXY_USER:
        case SubmissionRestriction.PROXY_USER:
          {
            return `Proxy users cannot ${actionLabel}`;
          }
        case ChatRestriction.NO_PERMISSION:
        case SubmissionRestriction.NO_PERMISSION:
          {
            return `You don't have permission to ${actionLabel}`;
          }
        case ChatRestriction.DISABLED:
          {
            return "Chat is currently disabled";
          }
        case SubmissionRestriction.NOT_STARTED:
          {
            return "Drop submissions haven't started yet";
          }
        case SubmissionRestriction.ENDED:
          {
            return "Drop submissions have ended";
          }
        case SubmissionRestriction.MAX_DROPS_REACHED:
          {
            return "You have reached the maximum number of drops allowed";
          }
        default:
          {
            return `${isChat ? inactiveActionLabel : "chat"} mode is unavailable`;
          }
      }
    };
    $[5] = inactiveActionLabel;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const getRestrictionMessage = t5;
  let t6;
  bb1: {
    if (isExitOnly) {
      let t7;
      if ($[7] !== exitLabel) {
        t7 = <span className="tw-text-xs">{exitLabel}</span>;
        $[7] = exitLabel;
        $[8] = t7;
      } else {
        t7 = $[8];
      }
      t6 = t7;
      break bb1;
    }
    if (!isDisabled && canToggle) {
      let t7;
      if ($[9] !== targetModeLabel) {
        t7 = <span className="tw-text-xs">Switch to {targetModeLabel} Mode</span>;
        $[9] = targetModeLabel;
        $[10] = t7;
      } else {
        t7 = $[10];
      }
      t6 = t7;
      break bb1;
    }
    if (targetRestriction !== null) {
      const t7 = getRestrictionMessage(targetRestriction, !isDropMode);
      let t8;
      if ($[11] !== t7) {
        t8 = <span className="tw-text-xs">{t7}</span>;
        $[11] = t7;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      t6 = t8;
      break bb1;
    }
    let t7;
    if ($[13] !== targetModeLabel) {
      t7 = <span className="tw-text-xs">{targetModeLabel} mode is unavailable</span>;
      $[13] = targetModeLabel;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t6 = t7;
  }
  const tooltipContent = t6;
  let t7;
  if ($[15] !== isIdentityEntry) {
    t7 = isIdentityEntry ? <UserPlusIcon className="tw-h-4.5 tw-w-4.5 tw-flex-shrink-0 tw-transition-colors md:tw-h-5 md:tw-w-5" aria-hidden="true" /> : <svg className="tw-h-4.5 tw-w-4.5 tw-flex-shrink-0 tw-transition-colors md:tw-h-5 md:tw-w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z" /><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97" /></svg>;
    $[15] = isIdentityEntry;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== buttonClassName || $[18] !== handleToggleClick || $[19] !== isDisabled || $[20] !== t7) {
    t8 = <div><button type="button" onClick={handleToggleClick} disabled={isDisabled} className={buttonClassName} data-tooltip-id="drop-mode-toggle">{t7}</button></div>;
    $[17] = buttonClassName;
    $[18] = handleToggleClick;
    $[19] = isDisabled;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== tooltipContent) {
    t9 = <Tooltip id="drop-mode-toggle" place="top" offset={8} opacity={1} positionStrategy="fixed" style={TOOLTIP_STYLES}>{tooltipContent}</Tooltip>;
    $[22] = tooltipContent;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};